/**
 * Future Cartridge battle log — Phaser-wired DOM glass panel.
 *
 * Persistent during battle; shows the most recent 3 lines of action
 * commentary ("Pikachu used Quick Attack!", "It's super effective!",
 * "Caterpie fainted!"). Replaces the legacy Phaser Rectangle + m5x7
 * BitmapText surface in BattleScene.
 *
 * Non-interactive (pointer-events: none) — taps pass straight through
 * to whatever's behind. Anchored bottom-center via JS-side inline
 * style; sized to ~88 % of canvas width capped at 720 px.
 *
 * Lifecycle: BattleScene mounts on create() and destroys on
 * exitToWorld / exitToEndRun. Phaser SLEEP hides the DOM (battle
 * scene swaps out for end-run / world); WAKE re-renders the last
 * known lines.
 */
import Phaser from 'phaser';
import { Z_DIALOGUE } from './zLayers';
import { ACTION_STACK_TOP_PX } from './battleActions';

const PARENT_ID = 'game';
const MAX_LINES = 3;
/** Derived, not hand-synced: 12 px clear of the action stack's top
 *  edge (battleActions.ACTION_STACK_TOP_PX). */
const BOTTOM_INSET = ACTION_STACK_TOP_PX + 12; // 328
const MAX_WIDTH = 720;

/** Panel bottom edge, px from canvas bottom — consumed by the status
 *  panel + BattleScene sprite-clearance math. */
export const BATTLE_LOG_BOTTOM_PX = BOTTOM_INSET;
/** Estimated rendered height at the round-3 type scale: 3 lines ×
 *  (17 px × 1.5 lh) + 2 × 14 px padding ≈ 105. Estimate, not measure —
 *  the consumers need a layout-time constant, and the panel's content
 *  is a fixed 3-line window. */
const LOG_HEIGHT_ESTIMATE_PX = 105;
/** Log top edge, px from canvas bottom. BattleScene.layout caps the
 *  player sprite to clear this; the player status panel sits 12 px
 *  above it. */
export const BATTLE_LOG_TOP_ESTIMATE_PX =
  BATTLE_LOG_BOTTOM_PX + LOG_HEIGHT_ESTIMATE_PX; // 433

export interface BattleLogHandle {
  /** Append a line. Older lines beyond MAX_LINES are trimmed off the
   *  top so the panel only ever shows the most recent 3. */
  append(line: string): void;
  /** Empty the log and hide the panel (no surface visible until the
   *  next append). */
  clear(): void;
  /** Unmount + remove all listeners. Idempotent. */
  destroy(): void;
}

export function mountBattleLog(scene: Phaser.Scene): BattleLogHandle {
  const parent = document.getElementById(PARENT_ID) ?? document.body;

  const el = document.createElement('div');
  el.className = 'battle-log';
  // Sit one z-index below the dialogue overlay so a future BattleScene
  // dialogue migration can overlay the log without z-fighting.
  el.style.zIndex = String(Z_DIALOGUE - 1);
  el.style.display = 'none';

  parent.appendChild(el);

  let lines: string[] = [];

  function render(): void {
    el.textContent = lines.join('\n');
    el.style.display = lines.length > 0 ? 'block' : 'none';
  }

  function position(): void {
    const w = parent.clientWidth;
    const desired = Math.round(w * 0.88);
    if (desired > MAX_WIDTH) {
      el.style.left = `${Math.round((w - MAX_WIDTH) / 2)}px`;
      el.style.width = `${MAX_WIDTH}px`;
    } else {
      el.style.left = `${Math.round(w * 0.06)}px`;
      el.style.width = `${desired}px`;
    }
    el.style.bottom = `${BOTTOM_INSET}px`;
  }
  position();

  let resizeQueued = false;
  function onResize(): void {
    if (resizeQueued) return;
    resizeQueued = true;
    requestAnimationFrame(() => {
      resizeQueued = false;
      position();
    });
  }
  window.visualViewport?.addEventListener('resize', onResize);
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);

  const onSleep = (): void => {
    el.style.display = 'none';
  };
  const onWake = (): void => {
    render();
  };
  scene.events.on(Phaser.Scenes.Events.SLEEP, onSleep);
  scene.events.on(Phaser.Scenes.Events.WAKE, onWake);

  return {
    append(line): void {
      lines.push(line);
      if (lines.length > MAX_LINES) lines.shift();
      render();
    },
    clear(): void {
      lines = [];
      render();
    },
    destroy(): void {
      scene.events.off(Phaser.Scenes.Events.SLEEP, onSleep);
      scene.events.off(Phaser.Scenes.Events.WAKE, onWake);
      window.visualViewport?.removeEventListener('resize', onResize);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      el.remove();
    },
  };
}
